import { SEO, OrganizationStructuredData } from '@/components/SEO';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Users, Eye, Zap } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        pageType="custom"
        title="About GoCompareAI | Independent AI Platform Comparison"
        description="GoCompareAI is an independent AI platform comparison service. We are not affiliated with Go.Compare, GoCompare.com, or Future PLC. Learn about our mission to help businesses choose the right AI tools."
      />
      <OrganizationStructuredData />
      
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            About GoCompareAI
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            The independent AI platform comparison service helping businesses make informed decisions.
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Our Mission</h2>
          <p className="text-muted-foreground mb-4">
            GoCompareAI was founded with a simple mission: to be the trusted, independent voice in AI platform 
            selection—separating marketing hype from genuine capabilities.
          </p>
          <p className="text-muted-foreground mb-4">
            The AI marketplace is overwhelming. With hundreds of platforms launching every month, each claiming 
            to be the "best" solution, businesses struggle to make informed decisions. Technical specifications 
            and marketing jargon make genuine comparison nearly impossible for non-technical decision-makers.
          </p>
          <p className="text-muted-foreground">
            We solve this by providing transparent, unbiased comparisons using plain English explanations. 
            No jargon. No hidden agendas. Just honest, helpful guidance to find the right AI tools for your needs.
          </p>
        </section>

        {/* What We Do */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-6">What We Do</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <Eye className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Compare AI Platforms</h3>
                <p className="text-muted-foreground text-sm">
                  We maintain detailed profiles of 400+ AI platforms, from ChatGPT and Claude to specialized 
                  tools for coding, content creation, customer service, and more.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Zap className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Guided Recommendations</h3>
                <p className="text-muted-foreground text-sm">
                  Our 30-60 second wizard asks simple questions about your needs and matches you with 
                  suitable platforms—no technical knowledge required.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Shield className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Independent Assessment</h3>
                <p className="text-muted-foreground text-sm">
                  We clearly separate vendor-provided information from our independent assessments. 
                  All users see the same results—we don't sell preferential placement.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Users className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Educational Content</h3>
                <p className="text-muted-foreground text-sm">
                  Our blog and guides help you understand AI concepts, use cases, and best practices 
                  in plain English—no computer science degree needed.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Our Commitment to Independence</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-primary font-bold">✓</span>
              <p className="text-muted-foreground">
                <strong>No paid rankings:</strong> We don't sell preferential placement in our comparisons or recommendations.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-primary font-bold">✓</span>
              <p className="text-muted-foreground">
                <strong>Clear labelling:</strong> All vendor-provided information is clearly marked and separated from our independent assessment.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-primary font-bold">✓</span>
              <p className="text-muted-foreground">
                <strong>Consistent criteria:</strong> Every platform is evaluated using the same methodology and standards.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-primary font-bold">✓</span>
              <p className="text-muted-foreground">
                <strong>User privacy:</strong> We don't sell user data to vendors. Your searches and comparisons remain private.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="text-center mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Get in Touch</h2>
          <p className="text-muted-foreground mb-4">
            Have questions? Want to suggest a platform for review? We'd love to hear from you.
          </p>
          <p className="text-muted-foreground">
            Email us at <a href="mailto:info@gocompareai.com" className="text-primary hover:underline">info@gocompareai.com</a>
          </p>
        </section>

        {/* Independence Notice - Moved to bottom */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900 mb-2">
                  Independent & Unaffiliated
                </h2>
                <p className="text-amber-800">
                  <strong>GoCompareAI is completely independent.</strong> We are not affiliated with, endorsed by, 
                  or connected to Go.Compare, GoCompare.com, Future PLC, or any insurance/financial comparison website. 
                  Our focus is exclusively on artificial intelligence and AI platform comparisons.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
}
